import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { UploadCloud, FileText, Sparkles, Wand2, Loader2, AlertCircle, Star, ArrowRight, Check, MessageSquare, X, Play, Zap, Copy, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import AgentAvatar from "@/components/AgentAvatar";
import AgentActionChat from "@/components/AgentActionChat";
import { invalidateMarketplaceCache } from "@/lib/marketplaceStore";

const SAMPLE = `Alex Morgan — Senior Brand & Product Designer
9 years of experience. Ex-IDEO, ex-Stripe.
- Led brand systems for 3 unicorn launches
- Speaker at Config 2023
- Skills: Figma, Webflow, brand identity, design systems, motion, prototyping
- Tools: Figma, Webflow, After Effects, Notion
- Languages: English, Portuguese
- Clients: Loom, Notion, Linear, Webflow
Highlights:
- Grew Stripe Atlas signups +28% via redesign
- Built design system used by 120 engineers`;

export default function BuilderPage() {
  const location = useLocation();
  const fromAvni = location.state?.fromAvni;
  const prefill = location.state?.resumeText || "";

  const [resumeText, setResumeText] = useState(prefill);
  const [step, setStep] = useState(1); // 1 upload, 2 generating, 3 result
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showAvniBanner, setShowAvniBanner] = useState(Boolean(fromAvni && prefill));
  const fileRef = useRef();
  const nav = useNavigate();

  // Auto-scroll resume textarea into view when pre-filled from Avni
  useEffect(() => {
    if (fromAvni && prefill) {
      const el = document.querySelector('[data-testid="resume-textarea"]');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
      }
    }
  }, [fromAvni, prefill]);

  const handleFile = async (f) => {
    if (!f) return;
    setError(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResumeText(r.data.text);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not parse this file. Try PDF, DOCX or paste text.");
    }
  };

  const generate = async () => {
    if (resumeText.trim().length < 40) {
      setError("Please paste or upload a resume with at least a few lines of content.");
      return;
    }
    setError(null);
    setStep(2);
    try {
      const r = await api.post("/agent/generate", { resume_text: resumeText });
      setAgent(r.data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong generating your agent.");
      setStep(1);
    }
  };

  return (
    <div data-testid="builder-page" className="relative">
      <div className="mx-auto max-w-7xl px-5 md:px-8 pt-10 pb-24">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <span>/</span>
          <span className="text-slate-900">Build my agent</span>
        </div>

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/80 px-3 py-1 text-xs font-medium text-blue-700">
            <Sparkles size={14} /> Step {step} of 3
          </span>
          {fromAvni && (
            <span data-testid="from-avni-pill" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <MessageSquare size={12} /> Pre-filled from your chat with Avni
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 max-w-3xl">
          {step === 1 && "Upload your resume. We'll do the rest."}
          {step === 2 && "Crafting your autonomous AI freelancer…"}
          {step === 3 && `Meet ${agent?.name || "your new agent"}.`}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          {step === 1 && "Drop a PDF, DOCX, or paste your resume text. GPT-5.2 will build a hireable agent of you in under 30 seconds."}
          {step === 2 && "Parsing skills, designing services, calibrating pricing, and writing your agent's voice."}
          {step === 3 && "Confirm the service menu below — your agent goes live the moment you do, and starts scanning for client briefs."}
        </p>

        {showAvniBanner && step === 1 && (
          <div data-testid="avni-banner" className="mt-6 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 px-5 py-4 flex items-start gap-4">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white shrink-0">
              <MessageSquare size={18} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-slate-900">
                Avni packaged your chat into a starter resume.
              </p>
              <p className="text-sm text-slate-600 mt-0.5">
                Review the text below — tweak it if you like — then hit <span className="font-semibold text-blue-700">Build my agent</span>. We&apos;ll generate your full freelance profile in ₹.
              </p>
            </div>
            <button
              onClick={() => setShowAvniBanner(false)}
              data-testid="avni-banner-dismiss"
              className="shrink-0 text-slate-400 hover:text-slate-700 rounded-md p-1"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          {/* Left: input or status */}
          <div className="lg:col-span-7">
            {step !== 3 && (
              <div className="space-y-5">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
                  data-testid="resume-dropzone"
                  className={`rounded-2xl border-2 border-dashed p-8 transition ${
                    dragging ? "border-blue-500 bg-blue-50/50" : "border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex flex-col items-center text-center">
                    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 mb-4">
                      <UploadCloud size={26} />
                    </span>
                    <p className="font-display text-lg font-bold text-slate-900">Drop your resume here</p>
                    <p className="text-sm text-slate-500 mt-1">PDF, DOCX or TXT · up to 5 MB</p>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.docx,.txt,.md"
                      hidden
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid="resume-file-input"
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      data-testid="resume-file-button"
                      className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-900 text-white text-sm px-4 py-2.5 hover:bg-slate-800"
                    >
                      Choose file
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="flex-1 h-px bg-slate-200" />
                  OR PASTE BELOW
                  <span className="flex-1 h-px bg-slate-200" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                    <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2">
                      <FileText size={14} /> Resume text
                    </span>
                    <button
                      onClick={() => setResumeText(SAMPLE)}
                      data-testid="resume-sample-btn"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Use sample resume
                    </button>
                  </div>
                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    placeholder="Paste your resume here — roles, skills, tools, wins, years of experience…"
                    rows={12}
                    data-testid="resume-textarea"
                    className="w-full px-4 py-3 text-sm font-mono outline-none resize-y rounded-b-2xl"
                  />
                </div>

                {error && (
                  <div data-testid="builder-error" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
                    <AlertCircle size={16} className="mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  onClick={generate}
                  disabled={step === 2}
                  data-testid="generate-agent-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-base font-medium px-5 py-4 hover:bg-blue-700 hover:-translate-y-0.5 transition disabled:opacity-60 disabled:translate-y-0 shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
                >
                  {step === 2 ? (
                    <>
                      <Loader2 className="animate-spin" size={18} /> Building your agent…
                    </>
                  ) : (
                    <>
                      <Wand2 size={18} /> Build my agent with GPT-5.2
                    </>
                  )}
                </button>
              </div>
            )}

            {step === 3 && agent && <AgentResult agent={agent} />}
          </div>

          {/* Right: preview / transform visualization */}
          <div className="lg:col-span-5">
            <TransformPanel resumeText={resumeText} step={step} agent={agent} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TransformPanel({ resumeText, step, agent }) {
  const skills = (agent?.skills || extractKeywords(resumeText)).slice(0, 10);

  return (
    <div className="sticky top-24 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Skills detected</span>
          <span className="text-xs text-slate-400">{skills.length}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 min-h-[44px]">
          {skills.length === 0 ? (
            <p className="text-sm text-slate-400">Skills will appear here as you type or upload.</p>
          ) : (
            skills.map((s, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 animate-float" style={{ animationDelay: `${i * 80}ms` }}>
                {s}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Beam */}
      <div className="h-6 relative">
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px beam-line" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 relative">
        {(() => {
          const stepLabel = step === 3 ? "Ready" : step === 2 ? "Generating" : "Preview";
          return (
            <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {stepLabel}
            </span>
          );
        })()}
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Agent profile</p>

        <div className="mt-4 flex items-start gap-3">
          <AgentAvatar seed={agent?.avatar_seed || "preview"} size={56} />
          <div className="flex-1 min-w-0">
            <p className="font-display text-xl font-bold leading-tight">{agent?.name || "Your Agent"}</p>
            <p className="text-sm text-slate-500 truncate">{agent?.primary_role || "Skills-to-Services"}</p>
            <div className="mt-1 flex items-center gap-1 text-xs text-slate-600">
              <Star size={12} className="fill-yellow-400 text-yellow-400" />
              <span className="font-medium">{agent?.rating || 4.9}</span>
              <span className="text-slate-400">· new agent</span>
            </div>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-3">
          {agent?.tagline || "Drop your resume to see your agent's tagline, services, and pricing render here in real-time."}
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <Stat label="Hourly" value={agent ? `₹${(agent.hourly_rate || 0).toLocaleString("en-IN")}` : "—"} />
          <Stat label="Services" value={agent ? agent.services?.length : "—"} />
          <Stat label="Skills" value={agent ? agent.skills?.length : skills.length || "—"} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 py-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="font-display font-bold text-slate-900">{value}</p>
    </div>
  );
}

function AgentResult({ agent }) {
  const [publishState, setPublishState] = useState({ status: "idle", error: null, category: null }); // idle | publishing | published
  const [chatOpen, setChatOpen] = useState(false);
  // Service-menu confirmation → activation (owner token issued, zero auth)
  const [selected, setSelected] = useState(() => (agent.services || []).map(() => true));
  const [activation, setActivation] = useState({ status: "idle", token: null, error: null }); // idle | activating | active
  const [copied, setCopied] = useState(false);

  const toggleService = (i) => {
    if (activation.status === "active") return;
    setSelected((prev) => {
      const next = [...prev];
      // keep at least one service selected
      if (next[i] && next.filter(Boolean).length === 1) return prev;
      next[i] = !next[i];
      return next;
    });
  };

  const activate = async () => {
    setActivation({ status: "activating", token: null, error: null });
    try {
      const services = (agent.services || []).filter((_, i) => selected[i]);
      const r = await api.post(`/agent/${agent.id}/activate`, { services });
      localStorage.setItem("freelancity_owner_token", r.data.owner_token);
      setActivation({ status: "active", token: r.data.owner_token, error: null });
    } catch (e) {
      setActivation({ status: "idle", token: null, error: e?.response?.data?.detail || "Could not activate. Try again." });
    }
  };

  const ownerUrl = activation.token ? `${window.location.origin}/dashboard?token=${activation.token}` : "";
  const copyOwnerLink = async () => {
    try {
      await navigator.clipboard.writeText(ownerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const publish = async () => {
    setPublishState({ status: "publishing", error: null, category: null });
    try {
      const r = await api.post(`/agent/${agent.id}/publish`);
      invalidateMarketplaceCache();
      setPublishState({ status: "published", error: null, category: r.data.category });
    } catch (e) {
      setPublishState({ status: "idle", error: e?.response?.data?.detail || "Could not publish.", category: null });
    }
  };

  return (
    <div data-testid="agent-result" className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <AgentAvatar seed={agent.avatar_seed} size={72} />
          <div>
            <p className="font-display text-xl sm:text-2xl font-black text-slate-900">{agent.name}</p>
            <p className="text-slate-500">{agent.primary_role}</p>
            <p className="mt-2 text-slate-700">{agent.tagline}</p>
          </div>
        </div>
        <span className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-slate-400">Hourly</p>
          <p className="font-display text-xl sm:text-2xl font-black text-slate-900">₹{(agent.hourly_rate || 0).toLocaleString("en-IN")}</p>
        </span>
      </div>

      <p className="mt-5 text-slate-700 leading-relaxed">{agent.bio}</p>

      <div className="mt-5">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 mb-2">Service catalog</p>
          {activation.status !== "active" && (
            <p className="text-[11px] text-slate-400">Tap a service to include / exclude it</p>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {agent.services?.map((s, i) => (
            <button
              type="button"
              key={i}
              onClick={() => toggleService(i)}
              data-testid={`service-${i}`}
              className={`text-left rounded-xl border p-4 transition relative ${
                selected[i]
                  ? "border-blue-300 bg-blue-50/30 ring-1 ring-blue-100"
                  : "border-slate-200 bg-white opacity-50"
              } ${activation.status === "active" ? "cursor-default" : "cursor-pointer hover:border-blue-400"}`}
            >
              <span
                data-testid={`service-check-${i}`}
                className={`absolute top-3 right-3 grid place-items-center w-5 h-5 rounded-full border ${
                  selected[i] ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 text-transparent"
                }`}
              >
                <Check size={12} strokeWidth={3} />
              </span>
              <div className="flex items-center justify-between pr-7">
                <p className="font-display font-bold text-slate-900">{s.title}</p>
                <span className="font-display font-bold text-blue-600 whitespace-nowrap">₹{(s.price || 0).toLocaleString("en-IN")}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1">{s.description}</p>
              <p className="text-xs text-slate-400 mt-2">Delivery: {s.eta}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-1.5">
        {agent.skills?.map((s) => (
          <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{s}</span>
        ))}
      </div>

      {/* ACTIVATION — confirm menu, get owner link (zero auth) */}
      {activation.status !== "active" ? (
        <div className="mt-7">
          <button
            onClick={activate}
            disabled={activation.status === "activating"}
            data-testid="activate-agent-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-base font-bold px-5 py-4 hover:bg-blue-700 hover:-translate-y-0.5 transition disabled:opacity-60 disabled:translate-y-0 shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)]"
          >
            {activation.status === "activating" ? (
              <>
                <Loader2 className="animate-spin" size={18} /> Activating your agent…
              </>
            ) : (
              <>
                <Zap size={18} strokeWidth={2.4} /> Confirm service menu & activate agent
              </>
            )}
          </button>
          <p className="mt-2 text-center text-[11px] text-slate-400">
            {selected.filter(Boolean).length} of {agent.services?.length || 0} services selected · agent starts scanning client briefs immediately
          </p>
          {activation.error && (
            <p data-testid="activation-error" className="mt-2 text-sm text-red-600 text-center">{activation.error}</p>
          )}
        </div>
      ) : (
        <div data-testid="owner-link-card" className="mt-7 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-white to-emerald-50/40 p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700 inline-flex items-center gap-1.5">
            <span className="relative inline-flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-500/50 animate-ping" />
              <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
            </span>
            Agent is live · scanning for briefs
          </p>
          <h3 className="mt-2 font-display text-xl font-black tracking-tight text-slate-900">
            This is your private owner link.
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            No login, no password — this link is the <span className="font-bold text-slate-900">only key</span> to your agent's inbox, proposals and earnings. Save it somewhere safe.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <code data-testid="owner-link-value" className="flex-1 min-w-0 truncate rounded-md border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-700 font-mono leading-relaxed">
              {ownerUrl}
            </code>
            <button
              type="button"
              onClick={copyOwnerLink}
              data-testid="copy-owner-link-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white text-sm font-bold px-4 hover:bg-slate-800 transition"
            >
              {copied ? <Check size={14} strokeWidth={2.6} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <ShieldCheck size={13} strokeWidth={2.5} className="shrink-0" />
            Anyone with this link controls your agent — treat it like a password.
          </div>
          <a
            href={ownerUrl}
            data-testid="open-owner-dashboard-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 text-white text-sm font-bold px-5 py-3 hover:bg-emerald-700 transition"
          >
            Open my agent dashboard <ArrowRight size={15} strokeWidth={2.5} />
          </a>
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        {activation.status !== "active" && (
          <a
            href={`/dashboard?agent=${encodeURIComponent(agent.id)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="goto-dashboard-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white text-sm font-medium px-5 py-3 hover:bg-slate-800"
          >
            Preview dashboard <ArrowRight size={16} />
          </a>
        )}
        {publishState.status === "published" ? (
          <Link
            to={`/marketplace${publishState.category ? `?category=${encodeURIComponent(publishState.category)}` : ""}`}
            data-testid="view-on-marketplace-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium px-5 py-3 hover:bg-emerald-100"
          >
            <Check size={16} /> Published{publishState.category ? ` to ${publishState.category}` : ""} · View on marketplace
          </Link>
        ) : (
          <button
            onClick={publish}
            disabled={publishState.status === "publishing"}
            data-testid="publish-to-marketplace-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white text-sm font-medium px-5 py-3 text-slate-700 hover:border-blue-400 hover:text-blue-700 disabled:opacity-60"
          >
            {publishState.status === "publishing" ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Publishing…
              </>
            ) : (
              <>
                <Check size={16} /> Publish to marketplace
              </>
            )}
          </button>
        )}
        {publishState.status !== "published" && (
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            data-testid="watch-agent-in-action-btn"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-sm font-medium px-5 py-3 hover:bg-blue-700 shadow-[0_18px_38px_-18px_rgba(37,99,235,0.7)] hover:-translate-y-0.5 transition"
          >
            <Play size={15} strokeWidth={2.5} fill="currentColor" /> Watch agent in action
          </button>
        )}
      </div>

      {publishState.error && (
        <p data-testid="publish-error" className="mt-3 text-sm text-red-600">{publishState.error}</p>
      )}

      <AgentActionChat agent={agent} open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}

function extractKeywords(text) {
  if (!text) return [];
  const stop = new Set(["the","and","with","for","from","that","this","have","more","also","work","skills","ex","at","of","to","in","on","an","a","i","my","is","as","or","by","be","was","are","were","using","used","into"]);
  const words = text.toLowerCase().match(/[a-z][a-z0-9+.#-]{2,}/g) || [];
  const freq = {};
  for (const w of words) {
    if (stop.has(w)) continue;
    freq[w] = (freq[w] || 0) + 1;
  }
  return Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([w]) => w.replace(/\b\w/g, (c) => c.toUpperCase()));
}
